/**
 * Year helper utilities for managing fiscal years and date ranges
 */

/**
 * Gets the current fiscal year based on the current date.
 * Assumes fiscal year starts in September (month 8).
 * Adjust the threshold month if your fiscal year starts differently.
 *
 * @returns The current fiscal year
 */
export const getCurrentFiscalYear = (): number => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();

  // If we're in September or later, we're in the new fiscal year
  // Otherwise, we're still in the previous fiscal year
  return currentMonth >= 8 ? currentYear : currentYear - 1;
};

/**
 * Generates an array of available fiscal years based on a range
 * before the current fiscal year (no future years).
 *
 * @param range - Number of past years before (and including) the current fiscal year to include
 * @returns Array of fiscal years, sorted from oldest to most recent
 */
export const getAvailableYears = (range = 2): number[] => {
  const currentFiscalYear = getCurrentFiscalYear();
  const years: number[] = [];

  // Only include current year and past years (no future years)
  for (let i = -range; i <= 0; i++) {
    years.push(currentFiscalYear + i);
  }

  return years;
};

/**
 * Formats a fiscal year in the standard display format (e.g., "2024/25")
 *
 * @param year - The starting year of the fiscal year
 * @returns Formatted fiscal year string
 */
export const formatFiscalYear = (year: number): string => {
  return `${year}/${(year + 1).toString().slice(-2)}`;
};
